import React, { useEffect, useState } from 'react';
import {
  Device,
  KineticEnergyManagementSystem,
  useKineticEnergyManagementSystemDetailQuery,
  useKineticEnergyManagementSystemDetailUpdatesSubscription,
} from '../../../../types/generated-types';
import DeviceLoadingSkeleton from '../shared-ui/deviceLoadingSkeleton';
import { formatDeviceMetric } from '../../../utils/formatters';
import { DeviceInformation } from '../shared-ui/device-info';
import { PanelFrame, ViewMode } from '../shared-ui/panel-frame';
import { Box, Grid, Typography } from '@mui/material';
import moment from 'moment';
import { updateCacheFromSubscriptionEvent } from '../../../../helpers/subscriptionUtils';

interface KineticEnergyManagementSystemProps {
  id: string;
}

export const KineticEnergyManagementSystemComponent: React.FC<
  KineticEnergyManagementSystemProps
> = ({ id }) => {
  const [device, setDevice] =
    useState<Partial<KineticEnergyManagementSystem>>();
  const [deviceInfo, setDeviceInfo] = useState<DeviceInformation[]>([]);

  const { data } = useKineticEnergyManagementSystemDetailQuery({
    variables: { id },
  });

  useKineticEnergyManagementSystemDetailUpdatesSubscription({
    variables: { id },
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
  });

  useEffect(() => {
    if (data?.kineticEnergyManagementSystemById) {
      setDevice(
        data.kineticEnergyManagementSystemById as Partial<KineticEnergyManagementSystem>,
      );
    }
  }, [data]);

  useEffect(() => {
    if (device) {
      const info: DeviceInformation[] = [
        {
          'Device Identification': {
            'Device Name':
              device.name ?? 'Unnamed Kinetic Energy Management System',
            'Device ID': device.deviceId ?? 'Unidentified',
            'Device Type': device.typeDisplayName ?? 'Unknown',
            'Last Updated': moment(device.timestamp).fromNow(),
          },
          'System Metrics': {
            'Current Energy Stored': formatDeviceMetric({
              value: device.currentEnergyStored?.value ?? undefined,
              units: device.currentEnergyStored?.units ?? undefined,
            }),
            'Rotor Speed': formatDeviceMetric({
              value: device.rotorSpeed?.value ?? undefined,
              units: device.rotorSpeed?.units ?? undefined,
            }),
            'Spin Down Time': formatDeviceMetric({
              value: device.spinDownTime?.value ?? undefined,
              units: device.spinDownTime?.units ?? undefined,
            }),
          },
          'System Status': {
            'Power State': device.powerState?.value ? 'On' : 'Off',
            'Is Powered On': device.isPoweredOn ? 'Yes' : 'No',
            'Is Configured': device.isConfigured ? 'Yes' : 'No',
            'Is Controlled': device.isControlled ? 'Yes' : 'No',
            'Has Pending Updates': device.hasPendingUpdates ? 'Yes' : 'No',
          },
          'System Integration': {
            Property: device.property?.title ?? 'Not assigned',
            Unit: device.unit?.name ?? 'Not assigned',
            Panel: device.panel?.displayName ?? 'Not assigned',
            Source: device.source?.name ?? 'Not assigned',
          },
        },
      ];
      setDeviceInfo(info);
    }
  }, [device]);

  if (!device) {
    return <DeviceLoadingSkeleton size="large" />;
  }

  return (
    <PanelFrame
      title="Kinetic Energy Management System"
      iconType="mdi:fan"
      device={device as Partial<Device>}
      infoEntries={deviceInfo}
      viewMode={ViewMode.NORMAL}
      editingEnabled={false}
    >
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              align="center"
            >
              Current Energy Stored
            </Typography>
            <Typography variant="h6" align="center">
              {formatDeviceMetric({
                value: device.currentEnergyStored?.value ?? undefined,
                units: device.currentEnergyStored?.units ?? undefined,
              })}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              align="center"
            >
              Rotor Speed
            </Typography>
            <Typography variant="h6" align="center">
              {formatDeviceMetric({
                value: device.rotorSpeed?.value ?? undefined,
                units: device.rotorSpeed?.units ?? undefined,
              })}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              align="center"
            >
              Spin Down Time
            </Typography>
            <Typography variant="h6" align="center">
              {formatDeviceMetric({
                value: device.spinDownTime?.value ?? undefined,
                units: device.spinDownTime?.units ?? undefined,
              })}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </PanelFrame>
  );
};
